package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import com.evoapps.evoshift.settings.LocalSettings
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Which "What's new" (SH-146) this device has seen. [release] names the notes in the app now
 * (WhatsNew.RELEASE): every build of a release shares them, so internal builds do not show them
 * again. A first install never shows them: it records the release at once. An install from
 * before the notes existed has no record yet; [usedBefore] (it remembers a workplace) tells it
 * apart from a first install.
 */
class WhatsNewHistory(
    private val store: LocalSettings,
    private val release: String,
    private val usedBefore: () -> Boolean
) {
    /** Whether to show the notes after this start; a first install is recorded as seen. */
    fun shouldShow(): Boolean {
        val seen = store.getString(KEY)
        if (seen == release) return false
        if (seen == null && !usedBefore()) {
            markSeen()
            return false
        }
        return true
    }

    fun markSeen() = store.putString(KEY, release)

    companion object {
        const val KEY = "whatsNewSeen"
    }
}

/**
 * The "What's new" sheet after an update (SH-146): decided once when the app starts (before
 * signing in, so a first install that signs in later still counts as one), shown once the
 * person is in; closing it marks the release as seen. Profile reopens the notes on its own.
 */
class WhatsNewViewModel(private val history: WhatsNewHistory) : ViewModel() {
    private val pending = MutableStateFlow(history.shouldShow())

    /** Whether the sheet is due (the app shows it once someone is signed in). */
    val due: StateFlow<Boolean> = pending.asStateFlow()

    fun dismiss() {
        history.markSeen()
        pending.value = false
    }
}
